import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import type { RelaySession, RelayMetrics } from '../types/relay';

export function useRelaySessions() {
  return useQuery<RelaySession[]>({
    queryKey: ['relay', 'sessions'],
    queryFn: async () => {
      const res = await api.get('/api/relay/sessions');
      return res.data;
    },
    refetchInterval: 10000,
  });
}

export function useRelaySession(id: string | undefined) {
  return useQuery<RelaySession>({
    queryKey: ['relay', 'sessions', id],
    queryFn: async () => {
      const res = await api.get(`/api/relay/sessions/${id}`);
      return res.data;
    },
    enabled: !!id,
    refetchInterval: 10000,
  });
}

export function useRelayMetrics() {
  return useQuery<RelayMetrics>({
    queryKey: ['relay', 'metrics'],
    queryFn: async () => {
      const res = await api.get('/api/relay/metrics');
      return res.data;
    },
    refetchInterval: 10000,
  });
}

export function useCreateRelaySession() {
  const queryClient = useQueryClient();
  return useMutation<RelaySession, Error, { tag?: string }>({
    mutationFn: async ({ tag }) => {
      const res = await api.post('/api/relay/sessions', { tag });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['relay', 'sessions'] });
    },
  });
}

export function useDeactivateRelaySession() {
  const queryClient = useQueryClient();
  return useMutation<RelaySession, Error, string>({
    mutationFn: async (id) => {
      const res = await api.post(`/api/relay/sessions/${id}/deactivate`);
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['relay', 'sessions'] });
      queryClient.invalidateQueries({ queryKey: ['relay', 'sessions', data.id] });
    },
  });
}

export function useDeleteRelaySession() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (id) => {
      await api.delete(`/api/relay/sessions/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['relay', 'sessions'] });
    },
  });
}
